import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../config/axios';
import { EmailAccount, EmailAccountFormData, ProcessedEmail, EmailStats } from '../../types/emailAccount';

interface EmailAccountState {
  accounts: EmailAccount[];
  processingHistory: ProcessedEmail[];
  stats: EmailStats | null;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
}

const initialState: EmailAccountState = {
  accounts: [],
  processingHistory: [],
  stats: null,
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
};

// Async thunks
export const fetchEmailAccounts = createAsyncThunk(
  'emailAccounts/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/email-accounts');
      return response.data.accounts;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch email accounts');
    }
  }
);

export const createEmailAccount = createAsyncThunk(
  'emailAccounts/create',
  async (data: EmailAccountFormData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/email-accounts', data);
      return response.data.account;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create email account');
    }
  }
);

export const updateEmailAccount = createAsyncThunk(
  'emailAccounts/update',
  async ({ id, data }: { id: number; data: Partial<EmailAccountFormData> }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/admin/email-accounts/${id}`, data);
      return response.data.account;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update email account');
    }
  }
);

export const deleteEmailAccount = createAsyncThunk(
  'emailAccounts/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await axios.delete(`/admin/email-accounts/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete email account');
    }
  }
);

export const testEmailConnection = createAsyncThunk(
  'emailAccounts/test',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/admin/email-accounts/${id}/test`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Connection test failed');
    }
  }
);

export const checkEmailAccount = createAsyncThunk(
  'emailAccounts/check',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/admin/email-accounts/${id}/check`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to trigger email check');
    }
  }
);

export const checkAllEmailAccounts = createAsyncThunk(
  'emailAccounts/checkAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/email-accounts/check-all');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to trigger email check');
    }
  }
);

export const fetchProcessingHistory = createAsyncThunk(
  'emailAccounts/fetchHistory',
  async ({ page = 1, limit = 50 }: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/email-processing-history', {
        params: { page, limit }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch processing history');
    }
  }
);

export const fetchEmailStats = createAsyncThunk(
  'emailAccounts/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/email-stats');
      return response.data.stats;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch email stats');
    }
  }
);

// Slice
const emailAccountSlice = createSlice({
  name: 'emailAccounts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch accounts
    builder.addCase(fetchEmailAccounts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEmailAccounts.fulfilled, (state, action: PayloadAction<EmailAccount[]>) => {
      state.loading = false;
      state.accounts = action.payload;
    });
    builder.addCase(fetchEmailAccounts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create account
    builder.addCase(createEmailAccount.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createEmailAccount.fulfilled, (state, action: PayloadAction<EmailAccount>) => {
      state.loading = false;
      state.accounts.unshift(action.payload);
    });
    builder.addCase(createEmailAccount.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update account
    builder.addCase(updateEmailAccount.fulfilled, (state, action: PayloadAction<EmailAccount>) => {
      const index = state.accounts.findIndex(a => a.id === action.payload.id);
      if (index !== -1) {
        state.accounts[index] = action.payload;
      }
    });

    // Delete account
    builder.addCase(deleteEmailAccount.fulfilled, (state, action: PayloadAction<number>) => {
      state.accounts = state.accounts.filter(a => a.id !== action.payload);
    });

    // Fetch processing history
    builder.addCase(fetchProcessingHistory.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchProcessingHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.processingHistory = action.payload.emails;
      state.currentPage = action.payload.pagination.page;
      state.totalPages = Math.ceil(action.payload.pagination.total / action.payload.pagination.limit);
    });
    builder.addCase(fetchProcessingHistory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch stats
    builder.addCase(fetchEmailStats.fulfilled, (state, action: PayloadAction<EmailStats>) => {
      state.stats = action.payload;
    });
  },
});

export const { clearError } = emailAccountSlice.actions;
export default emailAccountSlice.reducer;

